/**
 * Lead Integrity Engine — Cockpit skin
 * ────────────────────────────────────
 * Same engine, same selectors, same dialogs (Detail / Test / Config
 * are reused unchanged). Restructured shell: compact masthead with
 * delivery-rate hero, hairline KPI strip, hairline records table,
 * hairline review queue.
 *
 * No business logic changes. Every feature preserved:
 *   • Dashboard / Review Queue / All Records tabs
 *   • Test lead form, Rules config, Detail review with approve/reject
 *   • Disposition + source breakdown, rejection reasons
 */
import { useState, useMemo, useEffect } from "react";
import { useAppData } from "@/data/AppDataContext";
import {
  type IntakeRecord, type IntakeDisposition, type IntegrityConfig,
  DEFAULT_INTEGRITY_CONFIG, processIncomingLead, formatPhoneDisplay,
} from "@/data/leadIntegrityEngine";
import { LeadIntegrityService } from "@/services/leadIntegrity.service";
import type { Lead } from "@/data/pipelineTypes";
import { PROJECT_TYPES } from "@/data/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2, XCircle, AlertTriangle, Clock, Eye, Layers,
  Phone, Mail, MapPin, Settings2, Plus, Zap, ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BackToLeadPerformance } from "@/components/BackToLeadPerformance";
import { toast } from "sonner";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitTabs, CockpitSearch, CockpitListShell, CockpitColumns, CockpitRow,
  CockpitEmpty, SectionEyebrow,
} from "@/components/cockpit/CockpitShell";

const DISPOSITION_LABEL: Record<IntakeDisposition, string> = {
  approved: "Approved",
  approved_with_warning: "Warning",
  duplicate_flagged: "Duplicate",
  quarantined: "Quarantined",
  rejected: "Rejected",
};
const DISPOSITION_DOT: Record<IntakeDisposition, string> = {
  approved: "bg-success",
  approved_with_warning: "bg-warning",
  duplicate_flagged: "bg-warning",
  quarantined: "bg-primary",
  rejected: "bg-destructive",
};
const DISPOSITION_TONE: Record<IntakeDisposition, string> = {
  approved: "text-success",
  approved_with_warning: "text-warning",
  duplicate_flagged: "text-warning",
  quarantined: "text-primary",
  rejected: "text-destructive",
};

export default function LeadIntegrityCockpit({ embedded = false }: { embedded?: boolean } = {}) {
  const { leads, setLeads, leadSources } = useAppData();
  const [records, setRecords] = useState<IntakeRecord[]>([]);
  const [config, setConfig] = useState<IntegrityConfig>(DEFAULT_INTEGRITY_CONFIG);
  const [tab, setTab] = useState("dashboard");
  const [search, setSearch] = useState("");
  const [dispositionFilter, setDispositionFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [selectedRecord, setSelectedRecord] = useState<IntakeRecord | null>(null);
  const [showTestForm, setShowTestForm] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [testData, setTestData] = useState({
    primaryName: "", primaryPhone: "", primaryEmail: "",
    address: "", city: "", state: "FL", zip: "",
    projectType: "", leadSource: "",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await LeadIntegrityService.getAll();
      if (cancelled) return;
      setRecords(data ?? []);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    let result = records;
    if (dispositionFilter !== "all") result = result.filter(r => r.disposition === dispositionFilter);
    if (sourceFilter !== "all") result = result.filter(r => r.source === sourceFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.normalizedData.primaryName.toLowerCase().includes(q) ||
        r.normalizedData.primaryEmail.toLowerCase().includes(q) ||
        r.normalizedData.primaryPhone.includes(q) ||
        r.normalizedData.city.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q)
      );
    }
    return result;
  }, [records, dispositionFilter, sourceFilter, search]);

  const stats = useMemo(() => ({
    total: records.length,
    approved: records.filter(r => r.disposition === "approved").length,
    warning: records.filter(r => r.disposition === "approved_with_warning").length,
    duplicate: records.filter(r => r.disposition === "duplicate_flagged").length,
    quarantined: records.filter(r => r.disposition === "quarantined").length,
    rejected: records.filter(r => r.disposition === "rejected").length,
    avgScore: records.length ? Math.round(records.reduce((s, r) => s + r.totalScore, 0) / records.length) : 0,
    deliveryRate: records.length ? Math.round((records.filter(r => r.deliveredToPipeline).length / records.length) * 100) : 0,
  }), [records]);

  const reviewQueue = useMemo(
    () => records.filter(r => (r.disposition === "quarantined" || r.disposition === "duplicate_flagged") && !r.reviewedBy),
    [records]
  );

  const sourceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const source of leadSources) {
      const id = String(source.id ?? "").trim();
      const name = String(source.name ?? "").trim();
      if (id && name) map.set(id, name);
    }
    return map;
  }, [leadSources]);

  const sourceNameByNormalized = useMemo(() => {
    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const map = new Map<string, string>();
    for (const source of leadSources) {
      const name = String(source.name ?? "").trim();
      if (!name) continue;
      map.set(normalize(name), name);
    }
    return map;
  }, [leadSources]);

  const sourceNameByAlias = useMemo(() => {
    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const aliasToNames = new Map<string, Set<string>>();
    for (const source of leadSources) {
      const name = String(source.name ?? "").trim();
      if (!name) continue;
      const words = name.split(/\s+/).map((w) => w.trim()).filter(Boolean);
      const aliases = new Set<string>();
      aliases.add(normalize(name));
      if (words.length > 1) {
        aliases.add(normalize(words.map((w) => w[0]).join("")));
      }
      if (words.length === 1) {
        aliases.add(normalize(words[0].slice(0, 1)));
      }
      for (const alias of aliases) {
        if (!alias) continue;
        if (!aliasToNames.has(alias)) aliasToNames.set(alias, new Set<string>());
        aliasToNames.get(alias)!.add(name);
      }
    }
    const uniqueAliasMap = new Map<string, string>();
    for (const [alias, names] of aliasToNames.entries()) {
      if (names.size === 1) uniqueAliasMap.set(alias, [...names][0]);
    }
    return uniqueAliasMap;
  }, [leadSources]);

  const resolveCanonicalSourceName = (rawValue: string | null | undefined) => {
    const raw = String(rawValue ?? "").trim();
    if (!raw) return "Unknown";
    const byId = sourceNameById.get(raw);
    if (byId) return byId;
    const normalized = raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const byName = sourceNameByNormalized.get(normalized);
    if (byName) return byName;
    const byAlias = sourceNameByAlias.get(normalized);
    return byAlias ?? "Unmapped";
  };

  function handleTestSubmit() {
    if (!testData.primaryName || !testData.primaryPhone) {
      toast.error("Name and phone are required");
      return;
    }
    const result = processIncomingLead(testData, "manual", leads, config);
    setRecords(prev => [result, ...prev]);
    if (result.deliveredToPipeline) {
      const now = new Date().toISOString().split("T")[0];
      const newLead = {
        id: `LEAD-${String(leads.length + 1).padStart(3, "0")}`,
        displayReference: `OPP-${String(leads.length + 1).padStart(6, "0")}`,
        ...result.normalizedData,
        projectType: (PROJECT_TYPES.includes(result.normalizedData.projectType as any) ? result.normalizedData.projectType : "Other") as import("@/data/types").ProjectType,
        stage: "Lead" as const,
        stageHistory: [{ id: `sh-${Date.now()}`, fromStage: null, toStage: "Lead" as const, changedAt: now, changedBy: "System" }],
        assignedRep: "Unassigned",
        ownershipHistory: [{ id: `oh-${Date.now()}`, fromRep: null, toRep: "Unassigned", changedAt: now, changedBy: "Integrity Engine" }],
        activities: [{ id: `la-${Date.now()}`, type: "system" as const, description: `Lead accepted by Integrity Engine (Score: ${result.totalScore})`, createdAt: now, createdBy: "System" }],
        totalEstimate: 0, createdAt: now, updatedAt: now, notes: "",
      } as Lead;
      setLeads(prev => [newLead, ...prev]);
      setRecords(prev => prev.map(r => r.id === result.id ? { ...r, createdLeadId: newLead.id } : r));
      toast.success(`Lead approved and sent to pipeline as ${newLead.id}`);
    } else {
      toast.warning(`Lead ${result.disposition}: ${result.dispositionReason}`);
    }
    setShowTestForm(false);
    setTestData({ primaryName: "", primaryPhone: "", primaryEmail: "", address: "", city: "", state: "FL", zip: "", projectType: "", leadSource: "" });
  }

  function handleReviewAction(record: IntakeRecord, action: "approve" | "reject") {
    const now = new Date().toISOString();
    if (action === "approve") {
      const nowDate = now.split("T")[0];
      const newLead = {
        id: `LEAD-${String(leads.length + 1).padStart(3, "0")}`,
        displayReference: `OPP-${String(leads.length + 1).padStart(6, "0")}`,
        ...record.normalizedData,
        projectType: (PROJECT_TYPES.includes(record.normalizedData.projectType as any) ? record.normalizedData.projectType : "Other") as import("@/data/types").ProjectType,
        stage: "Lead" as const,
        stageHistory: [{ id: `sh-${Date.now()}`, fromStage: null, toStage: "Lead" as const, changedAt: nowDate, changedBy: "System" }],
        assignedRep: "Unassigned",
        ownershipHistory: [{ id: `oh-${Date.now()}`, fromRep: null, toRep: "Unassigned", changedAt: nowDate, changedBy: "Admin Review" }],
        activities: [{ id: `la-${Date.now()}`, type: "system" as const, description: `Manually approved from review queue (Score: ${record.totalScore})`, createdAt: nowDate, createdBy: "Admin" }],
        totalEstimate: 0, createdAt: nowDate, updatedAt: nowDate, notes: reviewNote || "",
      } as Lead;
      setLeads(prev => [newLead, ...prev]);
      setRecords(prev => prev.map(r =>
        r.id === record.id
          ? { ...r, disposition: "approved" as IntakeDisposition, reviewedBy: "Admin", reviewedAt: now, reviewNote, deliveredToPipeline: true, createdLeadId: newLead.id }
          : r
      ));
      toast.success(`Lead approved and pushed to pipeline as ${newLead.id}`);
    } else {
      setRecords(prev => prev.map(r =>
        r.id === record.id
          ? { ...r, disposition: "rejected" as IntakeDisposition, reviewedBy: "Admin", reviewedAt: now, reviewNote, deliveredToPipeline: false }
          : r
      ));
      toast.success("Lead rejected");
    }
    setSelectedRecord(null);
    setReviewNote("");
  }

  const recordsTemplate = "0.7fr 1.6fr 1fr 0.7fr 0.9fr 0.9fr 0.9fr 0.7fr auto";

  return (
    <CockpitPage>
      {!embedded && (
        <CockpitMasthead
          back={<BackToLeadPerformance />}
          eyebrow="Marketing · Validation gateway"
          title="Lead Integrity Engine"
          right={
            <>
              <HeroMetric label="Delivery rate" value={`${stats.deliveryRate}%`} hero tone={stats.deliveryRate >= 70 ? "good" : "warn"} />
              <HeroDivider />
              <HeroMetric label="Total" value={stats.total} />
              <HeroMetric label="Approved" value={stats.approved} tone="good" />
              <HeroMetric label="Quarantined" value={stats.quarantined} />
              <HeroMetric label="Rejected" value={stats.rejected} tone={stats.rejected > 0 ? "bad" : "default"} />
              <HeroMetric label="Avg score" value={stats.avgScore} />
            </>
          }
        />
      )}

      {embedded && (() => {
        const kpis = [
          { label: "Delivery Rate", value: `${stats.deliveryRate}%`, icon: ShieldCheck, color: stats.deliveryRate >= 70 ? "text-success" : "text-warning" },
          { label: "Total Leads", value: stats.total, icon: Layers, color: "text-foreground" },
          { label: "Approved", value: stats.approved, icon: CheckCircle2, color: "text-success" },
          { label: "Warning", value: stats.warning, icon: AlertTriangle, color: "text-warning" },
          { label: "Duplicates", value: stats.duplicate, icon: Layers, color: "text-warning" },
          { label: "Quarantined", value: stats.quarantined, icon: Clock, color: "text-primary" },
          { label: "Rejected", value: stats.rejected, icon: XCircle, color: stats.rejected > 0 ? "text-destructive" : "text-muted-foreground" },
          { label: "Avg Score", value: stats.avgScore, icon: Zap, color: stats.avgScore >= 80 ? "text-success" : stats.avgScore >= 55 ? "text-warning" : "text-destructive" },
          { label: "Review Queue", value: reviewQueue.length, icon: Eye, color: reviewQueue.length > 0 ? "text-warning" : "text-muted-foreground" },
        ];
        return (
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-px bg-border/60 border border-border/60 rounded-sm overflow-hidden">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="bg-background px-3 py-2.5 text-center">
                <kpi.icon className={`h-3.5 w-3.5 mx-auto mb-1 ${kpi.color}`} />
                <p className="text-base font-semibold leading-none tabular-nums">{kpi.value}</p>
                <p className="text-[8px] text-muted-foreground mt-1 font-medium uppercase tracking-wider">{kpi.label}</p>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="flex items-center justify-between gap-4">
        <CockpitTabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "dashboard", label: "Dashboard" },
            { value: "queue", label: "Review queue", count: reviewQueue.length },
            { value: "history", label: "All records", count: records.length },
          ]}
        />
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" className="h-9 text-sm bg-transparent border-border/60" onClick={() => setShowConfig(true)}>
            <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Rules
          </Button>
          <Button size="sm" className="h-9 text-sm" onClick={() => setShowTestForm(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Test lead
          </Button>
        </div>
      </div>

      {/* ─── Dashboard tab ─────────────────────────────────────────── */}
      {tab === "dashboard" && (
        <div className="grid md:grid-cols-2 gap-8">
          <section className="space-y-3">
            <SectionEyebrow>Disposition breakdown</SectionEyebrow>
            <div className="border-y border-border/60 divide-y divide-border/60">
              {(Object.keys(DISPOSITION_LABEL) as IntakeDisposition[]).map(key => {
                const count = records.filter(r => r.disposition === key).length;
                const pct = records.length ? Math.round((count / records.length) * 100) : 0;
                return (
                  <div key={key} className="flex items-center gap-4 py-3">
                    <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", DISPOSITION_DOT[key])} />
                    <span className="text-sm text-foreground/90 w-28">{DISPOSITION_LABEL[key]}</span>
                    <Progress value={pct} className="flex-1 h-1" />
                    <span className="text-sm tabular-nums text-foreground font-medium w-20 text-right">{count} <span className="text-muted-foreground/70">· {pct}%</span></span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="space-y-3">
            <SectionEyebrow>Source breakdown</SectionEyebrow>
            <div className="border-y border-border/60 divide-y divide-border/60">
              {(() => {
                const sources: Record<string, number> = {};
                records.forEach((r) => {
                  const sourceName = resolveCanonicalSourceName(r.normalizedData.leadSource || r.source);
                  const key = sourceName === "Unmapped" ? "Unmapped (needs mapping)" : sourceName;
                  sources[key] = (sources[key] || 0) + 1;
                });
                return Object.entries(sources).sort((a, b) => b[1] - a[1]).map(([src, count]) => {
                  const pct = Math.round((count / records.length) * 100);
                  return (
                    <div key={src} className="flex items-center gap-4 py-3">
                      <span className="text-sm text-foreground/90 w-32 truncate">{src || "Unknown"}</span>
                      <Progress value={pct} className="flex-1 h-1" />
                      <span className="text-sm tabular-nums text-foreground font-medium w-20 text-right">{count} <span className="text-muted-foreground/70">· {pct}%</span></span>
                    </div>
                  );
                });
              })()}
            </div>
          </section>

          <section className="md:col-span-2 space-y-3">
            <SectionEyebrow>Rejection &amp; quarantine reasons</SectionEyebrow>
            <div className="border-y border-border/60 divide-y divide-border/60">
              {(() => {
                const reasons: Record<string, number> = {};
                records.filter(r => r.disposition === "rejected" || r.disposition === "quarantined")
                  .forEach(r => { reasons[r.dispositionReason] = (reasons[r.dispositionReason] || 0) + 1; });
                const entries = Object.entries(reasons).sort((a, b) => b[1] - a[1]);
                if (entries.length === 0) return <p className="py-6 text-sm text-muted-foreground text-center">No rejections or quarantines.</p>;
                return entries.map(([reason, count]) => (
                  <div key={reason} className="flex items-center justify-between py-3">
                    <span className="text-sm text-foreground/90">{reason}</span>
                    <span className="text-sm tabular-nums text-foreground font-medium">{count}</span>
                  </div>
                ));
              })()}
            </div>
          </section>
        </div>
      )}

      {/* ─── Review queue tab ──────────────────────────────────────── */}
      {tab === "queue" && (
        <section>
          {reviewQueue.length === 0 ? (
            <div className="py-20 text-center">
              <ShieldCheck className="h-8 w-8 text-success mx-auto mb-3" />
              <p className="text-base text-foreground font-medium">Review queue clear</p>
              <p className="text-sm text-muted-foreground mt-1">No leads pending review.</p>
            </div>
          ) : (
            <CockpitListShell>
              <CockpitColumns template="0.7fr 1.6fr 1fr 1.4fr 0.7fr auto">
                <span>ID</span>
                <span>Contact</span>
                <span>Disposition</span>
                <span>Reason</span>
                <span className="text-right">Score</span>
                <span />
              </CockpitColumns>
              {reviewQueue.map(record => (
                <div key={record.id} className="grid grid-cols-[0.7fr_1.6fr_1fr_1.4fr_0.7fr_auto] gap-6 items-center px-1 py-3 hover:bg-muted/30 transition-colors">
                  <span className="text-sm font-medium text-foreground tabular-nums">{record.id}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{record.normalizedData.primaryName}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      {formatPhoneDisplay(record.normalizedData.primaryPhone)} · {record.normalizedData.city || "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", DISPOSITION_DOT[record.disposition])} />
                    <span className={cn("text-sm truncate", DISPOSITION_TONE[record.disposition])}>{DISPOSITION_LABEL[record.disposition]}</span>
                  </div>
                  <span className="text-sm text-muted-foreground truncate">{record.dispositionReason}</span>
                  <span className={cn(
                    "text-sm tabular-nums font-medium text-right",
                    record.totalScore >= 80 ? "text-success" : record.totalScore >= 55 ? "text-warning" : "text-destructive"
                  )}>{record.totalScore}</span>
                  <div className="flex gap-1.5 justify-self-end">
                    <Button size="sm" variant="outline" className="h-8 bg-transparent border-border/60" onClick={() => { setSelectedRecord(record); setReviewNote(""); }}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" className="h-8 bg-success hover:bg-success/90 text-white" onClick={() => handleReviewAction(record, "approve")}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="destructive" className="h-8" onClick={() => handleReviewAction(record, "reject")}>
                      <XCircle className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </CockpitListShell>
          )}
        </section>
      )}

      {/* ─── History tab ───────────────────────────────────────────── */}
      {tab === "history" && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <CockpitSearch value={search} onChange={setSearch} placeholder="Search records" />
            <Select value={dispositionFilter} onValueChange={setDispositionFilter}>
              <SelectTrigger className="h-9 w-44 text-sm bg-transparent border-border/60"><SelectValue placeholder="Disposition" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-sm">All dispositions</SelectItem>
                {Object.entries(DISPOSITION_LABEL).map(([k, v]) => <SelectItem key={k} value={k} className="text-sm">{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="h-9 w-36 text-sm bg-transparent border-border/60"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-sm">All sources</SelectItem>
                <SelectItem value="api" className="text-sm">API</SelectItem>
                <SelectItem value="webhook" className="text-sm">Webhook</SelectItem>
                <SelectItem value="web_form" className="text-sm">Web form</SelectItem>
                <SelectItem value="manual" className="text-sm">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <CockpitEmpty message="No records match the current filters." />
          ) : (
            <CockpitListShell>
              <CockpitColumns template={recordsTemplate}>
                <span>ID</span>
                <span>Name</span>
                <span>Disposition</span>
                <span className="text-right">Score</span>
                <span>Source</span>
                <span>Geo</span>
                <span>Pipeline</span>
                <span>Time</span>
                <span />
              </CockpitColumns>
              {filtered.map(record => (
                <CockpitRow
                  key={record.id}
                  template={recordsTemplate}
                  onClick={() => { setSelectedRecord(record); setReviewNote(""); }}
                  affordance={false}
                >
                  <span className="text-sm font-medium text-foreground tabular-nums">{record.id}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{record.normalizedData.primaryName}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{record.normalizedData.city}, {record.normalizedData.zip}</p>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", DISPOSITION_DOT[record.disposition])} />
                    <span className={cn("text-sm truncate", DISPOSITION_TONE[record.disposition])}>{DISPOSITION_LABEL[record.disposition]}</span>
                  </div>
                  <span className={cn(
                    "text-sm tabular-nums font-medium text-right",
                    record.totalScore >= 80 ? "text-success" : record.totalScore >= 55 ? "text-warning" : "text-destructive"
                  )}>{record.totalScore}</span>
                  <span className="text-sm text-muted-foreground capitalize">{record.source}</span>
                  <span className={cn("text-sm", record.geoResult === "in_area" ? "text-success" : record.geoResult === "out_of_area" ? "text-destructive" : "text-muted-foreground")}>
                    {record.geoResult === "in_area" ? "In area" : record.geoResult === "out_of_area" ? "Out of area" : "—"}
                  </span>
                  <span className={cn("text-sm", record.deliveredToPipeline ? "text-success" : "text-muted-foreground")}>
                    {record.deliveredToPipeline ? "Delivered" : "Blocked"}
                  </span>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {new Date(record.receivedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <Eye className="h-4 w-4 text-muted-foreground/40 group-hover:text-foreground transition-colors justify-self-end" />
                </CockpitRow>
              ))}
            </CockpitListShell>
          )}
        </section>
      )}

      {/* ─── Detail dialog (preserved) ─────────────────────────────── */}
      <Dialog open={!!selectedRecord} onOpenChange={open => { if (!open) setSelectedRecord(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedRecord && (() => {
            const r = selectedRecord;
            const needsReview = (r.disposition === "quarantined" || r.disposition === "duplicate_flagged") && !r.reviewedBy;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className={cn("h-2 w-2 rounded-full", DISPOSITION_DOT[r.disposition])} />
                    Intake record · {r.id}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Contact</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{r.normalizedData.primaryName}</span></div>
                      <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{formatPhoneDisplay(r.normalizedData.primaryPhone)}</span></div>
                      <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{r.normalizedData.primaryEmail || "—"}</span></div>
                      <div><span className="text-muted-foreground">Location:</span> <span className="font-medium">{r.normalizedData.city}, {r.normalizedData.state} {r.normalizedData.zip}</span></div>
                      <div><span className="text-muted-foreground">Project:</span> <span className="font-medium">{r.normalizedData.projectType || "—"}</span></div>
                      <div><span className="text-muted-foreground">Source:</span> <span className="font-medium">{r.normalizedData.leadSource}</span></div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Quality score · {r.totalScore}/100</h4>
                    <div className="space-y-1.5">
                      {r.scoreBreakdown.map(b => (
                        <div key={b.factor} className="flex items-center gap-2">
                          <span className="text-xs w-32 text-muted-foreground">{b.factor}</span>
                          <Progress value={(b.points / b.max) * 100} className="flex-1 h-1.5" />
                          <span className="text-xs font-mono w-10 text-right">{b.points}/{b.max}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Validations</h4>
                    <div className="space-y-1">
                      {r.validationResults.map((v, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          {v.status === "valid" && <CheckCircle2 className="h-3 w-3 text-success" />}
                          {v.status === "suspicious" && <AlertTriangle className="h-3 w-3 text-warning" />}
                          {v.status === "invalid" && <XCircle className="h-3 w-3 text-destructive" />}
                          {v.status === "missing" && <XCircle className="h-3 w-3 text-muted-foreground" />}
                          <span className="text-muted-foreground">{v.field}:</span>
                          <span>{v.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {r.duplicateMatches.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Duplicate matches</h4>
                        {r.duplicateMatches.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs bg-warning/10 border border-warning/30 rounded p-2 mb-1">
                            <Layers className="h-3 w-3 text-warning" />
                            <span>{d.type.replace(/_/g, " ")} — {d.matchedName} ({d.matchedLeadId}) — {d.confidence}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <Separator />
                  <div>
                    <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Decision</h4>
                    <div className="flex items-center gap-2">
                      <span className={cn("h-1.5 w-1.5 rounded-full", DISPOSITION_DOT[r.disposition])} />
                      <span className={cn("text-sm font-medium", DISPOSITION_TONE[r.disposition])}>{DISPOSITION_LABEL[r.disposition]}</span>
                      <span className="text-sm text-muted-foreground">— {r.dispositionReason}</span>
                    </div>
                    {r.reviewedBy && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Reviewed by {r.reviewedBy} at {new Date(r.reviewedAt!).toLocaleString()}
                        {r.reviewNote && <> — "{r.reviewNote}"</>}
                      </p>
                    )}
                  </div>
                  {needsReview && (
                    <>
                      <Separator />
                      <div>
                        <Label className="text-xs">Review note</Label>
                        <Textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)} placeholder="Optional note…" className="mt-1 h-16" />
                        <div className="flex gap-2 mt-3">
                          <Button className="flex-1 bg-success hover:bg-success/90 text-white" onClick={() => handleReviewAction(r, "approve")}>
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve &amp; push to pipeline
                          </Button>
                          <Button variant="destructive" className="flex-1" onClick={() => handleReviewAction(r, "reject")}>
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ─── Test lead dialog (preserved) ──────────────────────────── */}
      <Dialog open={showTestForm} onOpenChange={setShowTestForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Test lead intake</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={testData.primaryName} onChange={e => setTestData({ ...testData, primaryName: e.target.value })} className="h-9" placeholder="Full name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input value={testData.primaryPhone} onChange={e => setTestData({ ...testData, primaryPhone: e.target.value })} className="h-9" placeholder="(813) 555-1234" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={testData.primaryEmail} onChange={e => setTestData({ ...testData, primaryEmail: e.target.value })} className="h-9" placeholder="email@example.com" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={testData.address} onChange={e => setTestData({ ...testData, address: e.target.value })} className="h-9" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={testData.city} onChange={e => setTestData({ ...testData, city: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={testData.state} onChange={e => setTestData({ ...testData, state: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">ZIP *</Label>
                <Input value={testData.zip} onChange={e => setTestData({ ...testData, zip: e.target.value })} className="h-9" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Project type</Label>
                <Select value={testData.projectType} onValueChange={v => setTestData({ ...testData, projectType: v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {[...PROJECT_TYPES, "Roofing", "HVAC"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Lead source</Label>
                <Select value={testData.leadSource} onValueChange={v => setTestData({ ...testData, leadSource: v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {["Google Ads", "Facebook Ads", "Referral", "Home Show", "Website", "TV Ad", "Angi", "Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestForm(false)}>Cancel</Button>
            <Button onClick={handleTestSubmit}><Zap className="h-4 w-4 mr-1" /> Process lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Config dialog (preserved) ─────────────────────────────── */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings2 className="h-4 w-4" /> Integrity engine rules</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Score thresholds</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">High ≥</Label>
                  <Input type="number" value={config.scoreThresholds.high} className="h-9"
                    onChange={e => setConfig({ ...config, scoreThresholds: { ...config.scoreThresholds, high: parseInt(e.target.value) || 80 } })} />
                </div>
                <div>
                  <Label className="text-xs">Medium ≥</Label>
                  <Input type="number" value={config.scoreThresholds.medium} className="h-9"
                    onChange={e => setConfig({ ...config, scoreThresholds: { ...config.scoreThresholds, medium: parseInt(e.target.value) || 55 } })} />
                </div>
                <div>
                  <Label className="text-xs">Low ≥</Label>
                  <Input type="number" value={config.scoreThresholds.low} className="h-9"
                    onChange={e => setConfig({ ...config, scoreThresholds: { ...config.scoreThresholds, low: parseInt(e.target.value) || 30 } })} />
                </div>
              </div>
            </div>
            <Separator />
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Duplicate window</h4>
              <div className="flex items-center gap-2">
                <Input type="number" value={config.duplicateWindowHours} className="h-9 w-24"
                  onChange={e => setConfig({ ...config, duplicateWindowHours: parseInt(e.target.value) || 72 })} />
                <span className="text-sm text-muted-foreground">hours</span>
              </div>
            </div>
            <Separator />
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Allowed project types</h4>
              <div className="flex flex-wrap gap-1.5">
                {config.allowedProjectTypes.map(t => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
              </div>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Rejected project types</h4>
              <div className="flex flex-wrap gap-1.5">
                {config.rejectedProjectTypes.map(t => <Badge key={t} variant="outline" className="text-xs text-destructive border-destructive/30">{t}</Badge>)}
              </div>
            </div>
            <Separator />
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">Approved service areas ({config.approvedZipCodes.length} ZIP codes)</h4>
              <div className="flex flex-wrap gap-1.5">
                {config.approvedCities.map(c => <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CockpitPage>
  );
}
